import type {
  ModuleID,
  ModulesConfig,
  VersionString,
  ViewModuleRef,
} from "@bestowinc/tinkertoy-core";

/**
 * Runtime shape of a {@link ViewModuleRef}'s `props` after callback resolution.
 *
 * `module()` types `props` as a record (or a callback returning one); FEP2-3157
 * will further tighten registry-backed prop checking. The convention at the
 * renderer layer is that every module's resolved props are a record.
 * {@link resolveModuleProps} coerces non-objects to `{}` so all downstream
 * consumers can rely on this shape.
 *
 * Branch modules put child {@link ViewModuleRef}s under the optional `slots` key.
 * Slot values are typed as {@link ViewModuleRef} to document intent;
 * {@link extractSlotModuleRefs} keeps a runtime {@link isModuleRef} filter as
 * defense-in-depth until `module()` enforces slot value types (FEP2-3157).
 */
export type ResolvedProps = Record<string, unknown> & {
  slots?: Record<string, ViewModuleRef>;
};

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Resolve a module ref's `props` field — invokes the callback form with the
 * view's {@link ModulesConfig}, otherwise passes through. Non-record values
 * are coerced to `{}` so consumers can rely on {@link ResolvedProps}.
 */
export function resolveModuleProps(
  ref: ViewModuleRef,
  config: ModulesConfig,
): ResolvedProps {
  const raw = typeof ref.props === "function" ? ref.props(config) : ref.props;
  return isObjectRecord(raw) ? raw : {};
}

export function isModuleRef(value: unknown): value is ViewModuleRef {
  return (
    typeof value === "object" &&
    value !== null &&
    "_tag" in value &&
    Reflect.get(value, "_tag") === "ModuleRef"
  );
}

/**
 * If resolved props include `slots` with at least one valid {@link ViewModuleRef}
 * value, return the remaining props (without `slots`) and the slot refs.
 * Otherwise `null`.
 */
export function extractSlotModuleRefs(
  resolvedProps: ResolvedProps,
): {
  rest: Record<string, unknown>;
  slotRefs: Record<string, ViewModuleRef>;
} | null {
  const rawSlots = resolvedProps.slots;
  if (!rawSlots) {
    return null;
  }
  const slotRefs: Record<string, ViewModuleRef> = {};
  for (const [key, value] of Object.entries(rawSlots)) {
    // Runtime guard — `module()` types `props: unknown` so slot values are not
    // enforced as ModuleRefs at authoring time (FEP2-3157).
    if (isModuleRef(value)) {
      slotRefs[key] = value;
    }
  }
  if (Object.keys(slotRefs).length === 0) {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- strip `slots` from spread
  const { slots: _s, ...rest } = resolvedProps;
  return { rest, slotRefs };
}

export function getModuleVersion(
  ref: ViewModuleRef,
  resolvedModuleVersions: Record<ModuleID, VersionString>,
): VersionString {
  return resolvedModuleVersions[ref.moduleId] ?? ref.minVersion;
}
